import { addMilliseconds, differenceInMilliseconds } from "date-fns"
import { getDeviceDate } from "@myvp/shared/src/clock/get-device-date"

export const adjustVirtualTime = (args: {
  /** The round trip time for the request that receives a `virtualNow`. */
  rtt: number
  /** The `virtualNow` recorded by the BE. */
  virtualNow: string | Date
  /** The UTC moment at which the BE recorded `virtualNow`. */
  serverNow: string | Date
}): Date => {
  const virtualNow = new Date(args.virtualNow)
  const serverNow = new Date(args.serverNow)
  /** The UTC moment at which the FE received `virtualNow`. */
  const clientNow = getDeviceDate()

  /**
   * The amount of time (in ms) that has elapsed since `virtualNow` was recorded
   * on the BE and received on the FE. This value won't be correct if the FE device's
   * clock is set up incorrectly (NTP is turned off, etc).
   */
  const clockOffset = differenceInMilliseconds(clientNow, serverNow)
  if (clockOffset > 0 && clockOffset < args.rtt) {
    // clock offset isn't too large, so the client's clock is probably set up correctly
    return addMilliseconds(virtualNow, clockOffset)
  } else {
    // clock offset is too large, fallback to using (rtt / 2) as the best approximation
    // for time that has elapsed from when virtualNow was recorded on the BE to being
    // received on the FE.
    return addMilliseconds(virtualNow, args.rtt / 2)
  }
}

export interface VirtualClockState {
  virtualNow: Date
  lastUpdatedAt: number
}
export const createVirtualClockUpdateTask = (
  updater: (update: (prevData: VirtualClockState) => VirtualClockState) => void
) => {
  const takeStep = () => {
    let timeoutMs: number | undefined = undefined
    updater((prev) => {
      const now = performance.now()
      const elapsed = now - prev.lastUpdatedAt
      const virtualNow = addMilliseconds(prev.virtualNow, elapsed)
      // try to calculate the amount of time left to tick a clock forward 1 second
      // add `+ 1` extra ms to help ensure we're on the next second when the timeout runs.
      // NOTE: we assume using the UTC timestamp generalizes to all timezones because
      // currently timezones use offsets that are increments of 15 minutes.
      timeoutMs = 1_000 - (virtualNow.getTime() % 1_000) + 1
      return { virtualNow, lastUpdatedAt: now }
    })
    if (timeoutMs !== undefined) {
      timeoutId = setTimeout(takeStep, timeoutMs)
    }
  }
  let timeoutId = setTimeout(takeStep, 0)
  return () => {
    clearTimeout(timeoutId)
  }
}
